import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import dayjs from 'dayjs';
import { getUserTickets, getGeneralProducts, updateLockerPassDate } from '../../../api/user';
import { assignLockerPassSpot, unassignLockerPassSpot, applyLockerPassPlan } from '../../../api/member-management';
import { errorMessages } from '../../../utils/methods';
import { getPointTrainers } from '../../../api/staff';
import ChangeTPeriodModal from './modals/ChangeTPeriodModal';
import ProductInfoEditModal from './modals/ProductInfoEditModal';
import ChangeTFreezyModal from './modals/ChangeTFreezyModal';
import ChangePFreezyModal from '../ChangePFreezyModal'; // 회원권 측에서 modal/setModal API로 사용
import ChangePassPeriodModal from './modals/ChangePassPeriodModal';
import SubscriptionCancellationModal from './modals/SubscriptionCancellationModal';
import ChangeInstructorModal from './modals/ChangeInstructorModal';
import ReservationSettingModal from './modals/ReservationSettingModal';
import UseBranchEditModal from './modals/UseBranchEditModal';
import TicketChangeHistoryModal from './modals/TicketChangeHistoryModal';
import ChangeTrialPeriodModal from './modals/ChangeTrialPeriodModal';
import ChangeLessonBatchModal from './modals/ChangeLessonBatchModal';
import FindLessonModal from './modals/FindLessonModal';
import TicketLessonHistoryModal from './modals/TicketLessonHistoryModal';
import ChangeGeneralProductPeriodModal from './modals/ChangeGeneralProductPeriodModal';
import ChangeGeneralProductStopModal from './modals/ChangeGeneralProductStopModal';
import BulkStopModal from './modals/BulkStopModal';
import PassCard from './cards/PassCard';
import CourseTicketCard from './cards/CourseTicketCard';
import TrialTicketCard from './cards/TrialTicketCard';
import ProductCard from './cards/ProductCard';
import LockerSpotCard from './cards/LockerSpotCard';
import LockerSelectModal from '../../SalesManagment/modal/enterSaleInfoModal/lockerSelectModal/LockerSelectModal';
import ConfirmModal from '../../common/modal/ConfirmModal';
import LockerAttachDateModal from '../../common/modal/LockerAttachDateModal';
import CommonLoading from '../../../form-components/CommonLoading';
import CategorySidebar from './CategorySidebar';

import {
  PageContainer,
  LayoutShell,
  MainContent,
  Zone,
  Section,
  SectionHeader,
  SectionTitle,
  SectionTotal,
  Subsection,
  SubsectionHeader,
  SubsectionTitle,
  CountBadge,
  CountBadgeGray,
  CardGrid,
  EmptyState,
  SectionHeaderAction,
  LeafActionRow,
  BulkStopButton,
} from './CoursTicketDetailStyled';

// 회원권 모달 boolean 키 — 자식 모달들이 modal.openX 형태로 직접 읽고 쓰는 컨트랙트라
// 키 이름은 유지하되 어떤 모달인지 주석으로 명확히 한다.
const INITIAL_MODAL_STATE = {
  open1: false, // 수강권 기간/횟수 변경
  open2: false, // 이용권 기간/횟수 변경
  open3: false, // 수강권 중지 설정
  open4: false, // 이용권 중지 설정
  open5: false, // 담당 강사 변경
  openReservationSetting: false, // 수강권 예약 설정
  open6: false, // 레슨 일괄예약
  open7: false, // 일일권 기간 변경
  openFindLesson: false, // 레슨 찾기 (일괄예약 진입 단계)
  openSubCancel: false, // 구독 해지 (이용권/수강권 공용)
  openProductInfo: false, // 상품 정보 수정 (기간·횟수 / 예약 설정 / 출입문 탭)
};

// 한 줄짜리 sub-section 렌더 헬퍼 — 5개 sub-section(이용권/일일권/수강권/라커/일반상품)이 같은 모양을 갖도록 통일.
const ProductSubsection = ({ title, count, items, emptyMessage, isExpired, renderItem, children }) => {
  const Badge = isExpired ? CountBadgeGray : CountBadge;
  return (
    <Subsection>
      <SubsectionHeader>
        <SubsectionTitle>{title}</SubsectionTitle>
        <Badge>{count}</Badge>
      </SubsectionHeader>
      {items.length > 0 ? (
        <CardGrid>
          {items.map(renderItem)}
          {children}
        </CardGrid>
      ) : (
        <EmptyState>{emptyMessage}</EmptyState>
      )}
    </Subsection>
  );
};

const CoursTicketDetail = forwardRef(({ user, bigUser, onTicketsUpdate, onLockerExtend, focusTicketId }, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const userApp = useSelector(state => state.user.user);

  const [ticket, setTicket] = useState();
  const [modal, setModal] = useState(INITIAL_MODAL_STATE);
  // 상품 정보 수정 모달 대상 종류·처음 열 탭
  const [productInfo, setProductInfo] = useState({ kind: 'course', tab: 'period' });
  // 구독 해지 대상 종류: 'COURSE_PASS'(이용권) | 'COURSE_TICKET'(수강권)
  const [subCancelType, setSubCancelType] = useState(null);
  const [ticketAnchor, setTicketAnchor] = useState(null);
  const [passAnchor, setPassAnchor] = useState(null);
  const [tab, setTab] = useState('all'); // 'all' | 'active' | 'upcoming' | 'expired'
  // 기본 탭 자동 선택을 이미 적용한 회원 id — 회원이 바뀔 때만 다시 판정한다.
  const defaultTabMemberIdRef = useRef(null);
  // 사이드바 카테고리 필터: 'all' | 'tickets' | 'products' | 'pass' | 'trial' | 'ticket' | 'locker' | 'product'
  const [category, setCategory] = useState('all');

  // 수강권/일일권 카드 클릭 → 예약·수업 내역 모달 대상. { kind: 'course' | 'trial', item }
  const [historyTarget, setHistoryTarget] = useState(null);

  // 일반상품(라커/상품) 측 모달 상태 — 회원권과 다른 open/onClose API.
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productPeriodModalOpen, setProductPeriodModalOpen] = useState(false);
  const [productStopModalOpen, setProductStopModalOpen] = useState(false);
  const [bulkStopModalOpen, setBulkStopModalOpen] = useState(false);
  // 라커 자리 배정/변경 대상 패스와 모달, 자리 묶음 펼침 상태(key = branchLockerId)
  const [lockerAssignTarget, setLockerAssignTarget] = useState(null);
  const [lockerAssignModalOpen, setLockerAssignModalOpen] = useState(false);
  // 배정 해제 확인 대상 — null 이 아니면 확인 모달 표시
  const [lockerUnassignTarget, setLockerUnassignTarget] = useState(null);
  // 본인 자리에 기간이 겹치는 패스를 붙일 때의 '이어서 배정' 대상 — { locker, item } | null
  const [lockerAttachTarget, setLockerAttachTarget] = useState(null);
  // 회원권별 이용지점 수동 조정 대상 — { kind, ticketId, productName, item }
  const [useBranchTarget, setUseBranchTarget] = useState(null);
  // 회원권 1건의 변경 이벤트 조회 대상 — 같은 형태
  const [changeHistoryTarget, setChangeHistoryTarget] = useState(null);

  const headBranchId = userApp?.headBranch?.headBranchId;
  const branchId = userApp?.branch?.branchId;
  const ticketsQueryKey = ['userTickets', user?.memberId, headBranchId, branchId];
  const productsQueryKey = ['generalProducts', user?.memberId, branchId];

  // 회원권 (이용권 / 일일권 / 수강권)
  const { data: userTickets, isLoading: isTicketsLoading } = useQuery({
    queryKey: ticketsQueryKey,
    queryFn: () =>
      getUserTickets(user.memberId, bigUser.token, `headBranchId=${headBranchId || ''}&branchId=${branchId || ''}`),
    enabled: !!user?.memberId && !!bigUser?.token,
  });

  // 지점 개인레슨 강사 id — 담당강사가 지점 강사 전원이면 카드에 '전체 강사'로 줄여 보여준다
  const { data: branchTrainerIds = [] } = useQuery({
    queryKey: ['branchPrivateTrainerIds', branchId],
    queryFn: async () => {
      const data = await getPointTrainers(`${branchId}&courseClassType=PRIVATE_LESSON`, bigUser.token);
      return data.map(item => item.trainerId);
    },
    enabled: !!branchId && !!bigUser?.token,
  });

  // 일반상품 (락커 / 일반상품)
  const { data: generalProducts, isLoading: isProductsLoading } = useQuery({
    queryKey: productsQueryKey,
    queryFn: () => getGeneralProducts(user.memberId, bigUser.token, branchId),
    enabled: !!user?.memberId && !!bigUser?.token && !!branchId,
  });

  // 자식 모달 mutation 성공 후 호출 — 두 쿼리 모두 갱신 + 부모 알림.
  // 라커 배정/해제는 자리 점유가 바뀌므로 라커 목록 캐시(['lockers'])도 함께 비운다.
  // 안 비우면 새로고침 전까지 배정 모달이 그 자리를 빈 자리로 보여준다.
  const refreshAfterMutation = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ticketsQueryKey }),
      queryClient.invalidateQueries({ queryKey: productsQueryKey }),
      queryClient.invalidateQueries({ queryKey: ['lockers'] }),
    ]);
    if (onTicketsUpdate) onTicketsUpdate();
  };

  // 예약 내역 모달에서 출결·취소를 처리하면 카드 수치가 바뀌므로, 열려 있는 모달이 들고 있는
  // 티켓 스냅샷도 갱신된 목록 기준으로 다시 잡아준다 (요약 바의 잔여/예약중이 멈춰 있지 않게).
  useEffect(() => {
    if (!historyTarget || !userTickets) return;
    const isTrial = historyTarget.kind === 'trial';
    const idKey = isTrial ? 'memberTrialTicketId' : 'memberCourseTicketId';
    const pool = isTrial
      ? [...(userTickets.activeTrialTickets ?? []), ...(userTickets.expiredTrialTickets ?? [])]
      : [...(userTickets.activeTickets ?? []), ...(userTickets.expiredTickets ?? [])];
    const fresh = pool.find(item => item[idKey] === historyTarget.item?.[idKey]);
    if (fresh && fresh !== historyTarget.item) {
      setHistoryTarget(prev => (prev ? { ...prev, item: fresh } : prev));
    }
  }, [userTickets]);

  // 부모(UserDetail)는 ref로 단순 새로고침만 호출.
  useImperativeHandle(ref, () => ({
    refreshData: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ticketsQueryKey }),
        queryClient.invalidateQueries({ queryKey: productsQueryKey }),
      ]),
  }));

  // active/expired 목록에서 id로 항목을 찾아 선택 상태로 둔다.
  const selectFromList = (list, id, idKey) => list?.find(item => item[idKey] === id);

  const handleTicketMenu = (event, id, ticketType) => {
    if (event) setTicketAnchor(event.currentTarget);
    const list = ticketType === 'expired' ? userTickets?.expiredTickets : userTickets?.activeTickets;
    setTicket(selectFromList(list, id, 'memberCourseTicketId'));
  };

  const handlePassMenu = (event, id, passType) => {
    if (event) setPassAnchor(event.currentTarget);
    const list = passType === 'expired' ? userTickets?.expiredPasses : userTickets?.activePasses;
    setTicket(selectFromList(list, id, 'memberCoursePassId'));
  };

  const handleTrialSelect = (id, trialType) => {
    const list = trialType === 'expired' ? userTickets?.expiredTrialTickets : userTickets?.activeTrialTickets;
    setTicket(selectFromList(list, id, 'memberTrialTicketId'));
  };

  const closeMenus = () => {
    setTicketAnchor(null);
    setPassAnchor(null);
  };

  // 모달 단일 진입점 — 회원권 측.
  const openModal = key => setModal(prev => ({ ...prev, [key]: true }));

  // 일반상품 카드의 "기간 변경" 버튼 핸들러.
  const handleProductPeriodChange = item => {
    setSelectedProduct(item);
    setProductPeriodModalOpen(true);
  };

  const closeProductPeriodModal = () => {
    setProductPeriodModalOpen(false);
    setSelectedProduct(null);
  };

  // 라커 카드의 "라커 배정" / "자리 변경" 버튼 핸들러.
  const handleLockerAssign = item => {
    setLockerAssignTarget(item);
    setLockerAssignModalOpen(true);
  };

  const closeLockerAssignModal = () => {
    setLockerAssignModalOpen(false);
    setLockerAssignTarget(null);
  };

  const handleLockerSelected = async (locker, passIds = []) => {
    if (!lockerAssignTarget) return;
    // 같은 자리를 다시 고른 경우 — 바꿀 것이 없으니 그대로 닫는다.
    if (lockerAssignTarget.branchLockerId && locker.lockerId === lockerAssignTarget.branchLockerId) {
      return;
    }
    // 옮길 이용권들 — 체크박스로 고른 것(없으면 이 카드 하나).
    // 만료도 함께 옮긴다(연체는 회원을 따라간다). 기간은 합치기 큐에서 고정된다.
    const movingPasses = (passIds.length > 0
      ? lockerPasses.filter(lp => passIds.includes(lp.memberLockerPassId))
      : [lockerAssignTarget]
    ).filter(Boolean).map(lp => ({ ...lp, expired: isExpiredLocker(lp) }));

    // 본인이 쓰던 자리는 겹침 여부·개수와 무관하게 '합치기' 확인 창으로 유도 —
    // 기존 종료일 다음 날부터 이용 일수를 유지하며 순서대로 이어 붙인다(수정 가능).
    const isOwnSpot = locker.memberId === user?.memberId;
    if (isOwnSpot) {
      setLockerAttachTarget({ locker, passes: movingPasses, sourceLockerId: lockerAssignTarget.branchLockerId });
      closeLockerAssignModal();
      return;
    }
    // 빈 자리로 옮길 때는 기간 그대로 배정한다.
    try {
      for (const lp of movingPasses) {
        await assignLockerPassSpot(bigUser.token, user.memberId, lp.memberLockerPassId, locker.lockerId);
      }
      toast.success(t('라커를 배정했습니다'));
      closeLockerAssignModal();
      await refreshAfterMutation();
    } catch (error) {
      // 같은 자리 기간 겹침·타 회원 점유는 서버 메시지를 그대로 노출한다.
      toast.error(errorMessages(error));
      await refreshAfterMutation();
      // false 반환 → 자리 선택 모달이 닫히지 않고 다른 자리를 바로 고를 수 있다.
      return false;
    }
  };

  const handleLockerUnassign = item => setLockerUnassignTarget(item);

  const confirmLockerUnassign = async () => {
    const item = lockerUnassignTarget;
    setLockerUnassignTarget(null);
    if (!item) return;
    try {
      await unassignLockerPassSpot(bigUser.token, user.memberId, item.memberLockerPassId);
      toast.success(t('라커 배정을 해제했습니다'));
      await refreshAfterMutation();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };


  // '이어서 배정' 확정 — 모달이 계산한 기간과 자리를 한 번에 반영한다.
  // 배정 API 가 기간까지 함께 받으므로(기간수정 → 이동 2단계 아님) 원래 자리의
  // 형제 이용권과 부딪히는 중간 상태 없이 여러 개를 순서대로 옮길 수 있다.
  const closeLockerAttach = () => setLockerAttachTarget(null);

  const confirmLockerAttach = async entries => {
    if (!lockerAttachTarget || !entries?.length) return;
    const { locker } = lockerAttachTarget;
    try {
      await applyLockerPassPlan(
        bigUser.token,
        user.memberId,
        locker.lockerId,
        entries.map(entry => ({
          memberLockerPassId: entry.memberLockerPassId,
          startDate: dayjs(entry.startDate).format('YYYY-MM-DD'),
          expireDate: dayjs(entry.expireDate).format('YYYY-MM-DD'),
        }))
      );
      toast.success(t('기간을 이어서 라커를 배정했습니다'));
      closeLockerAttach();
      await refreshAfterMutation();
    } catch (error) {
      toast.error(errorMessages(error));
      await refreshAfterMutation();
    }
  };

  // 일반상품 카드의 "중지 설정" 버튼 핸들러.
  const handleProductStop = item => {
    setSelectedProduct(item);
    setProductStopModalOpen(true);
  };

  const closeProductStopModal = () => {
    setProductStopModalOpen(false);
    setSelectedProduct(null);
  };

  // 사용예정: BE의 active 목록 중 시작일이 미래인 항목.
  //   회원권은 issueDate, 일반상품은 startDate 필드를 사용한다.
  const today = dayjs().startOf('day');
  const isUpcomingByIssueDate = item => !!item?.issueDate && today.isBefore(dayjs(item.issueDate).startOf('day'));
  const isUpcomingByStartDate = item => !!item?.startDate && today.isBefore(dayjs(item.startDate).startOf('day'));

  // 회원권 분류 — active 안에서 사용예정/실제사용중을 분리한다.
  const activePassesAll = userTickets?.activePasses ?? [];
  const activeTrialTicketsAll = userTickets?.activeTrialTickets ?? [];
  const activeTicketsAll = userTickets?.activeTickets ?? [];
  const upcomingPasses = activePassesAll.filter(isUpcomingByIssueDate);
  const upcomingTrialTickets = activeTrialTicketsAll.filter(isUpcomingByIssueDate);
  const upcomingTickets = activeTicketsAll.filter(isUpcomingByIssueDate);
  const activePasses = activePassesAll.filter(item => !isUpcomingByIssueDate(item));
  const activeTrialTickets = activeTrialTicketsAll.filter(item => !isUpcomingByIssueDate(item));
  const activeTickets = activeTicketsAll.filter(item => !isUpcomingByIssueDate(item));
  const expiredPasses = userTickets?.expiredPasses ?? [];
  const expiredTrialTickets = userTickets?.expiredTrialTickets ?? [];
  const expiredTickets = userTickets?.expiredTickets ?? [];
  const ticketsActiveTotal = activePasses.length + activeTrialTickets.length + activeTickets.length;
  const ticketsUpcomingTotal = upcomingPasses.length + upcomingTrialTickets.length + upcomingTickets.length;
  const ticketsExpiredTotal = expiredPasses.length + expiredTrialTickets.length + expiredTickets.length;

  // 일반상품 분류 — 회원권(BE)과 동일한 기준으로 맞춘다.
  //   사용 중: ACTIVE / STOPPED
  //   만료됨: INACTIVE
  //   TRANSFERRED / REFUNDED 등은 어디에도 노출하지 않는다.
  const lockerPasses = generalProducts?.lockerPasses ?? [];
  const productPasses = generalProducts?.productPasses ?? [];
  const isActiveStatus = status => status === 'ACTIVE' || status === 'STOPPED';
  const isExpiredStatus = status => status === 'INACTIVE';
  // 라커는 상태가 ACTIVE 여도 종료일이 지났으면 만료로 본다 (스케줄러가 INACTIVE 로 바꾸기 전까지
  // 사용 중 목록에 남아 연체처럼 보이는 것을 막는다).
  const isExpiredLocker = item =>
    isExpiredStatus(item.status) ||
    (item.expireDate && dayjs(item.expireDate).isBefore(dayjs().startOf('day'), 'day'));
  const activeLockersAll = lockerPasses.filter(
    item => isActiveStatus(item.status) && !isExpiredLocker(item)
  );
  const activePeriodPassesAll = productPasses.filter(pass => isActiveStatus(pass.status));
  const upcomingLockers = activeLockersAll.filter(isUpcomingByStartDate);
  const upcomingPeriodPasses = activePeriodPassesAll.filter(isUpcomingByStartDate);
  const activeLockers = activeLockersAll.filter(item => !isUpcomingByStartDate(item));
  const activePeriodPasses = activePeriodPassesAll.filter(pass => !isUpcomingByStartDate(pass));
  const expiredLockers = lockerPasses.filter(isExpiredLocker);
  const expiredPeriodPasses = productPasses.filter(pass => isExpiredStatus(pass.status));
  const productsActiveTotal = activeLockers.length + activePeriodPasses.length;
  const productsUpcomingTotal = upcomingLockers.length + upcomingPeriodPasses.length;
  const productsExpiredTotal = expiredLockers.length + expiredPeriodPasses.length;

  // 탭 라벨에 표시할 종합 카운트.
  const activeTotal = ticketsActiveTotal + productsActiveTotal;
  const upcomingTotal = ticketsUpcomingTotal + productsUpcomingTotal;
  const expiredTotal = ticketsExpiredTotal + productsExpiredTotal;

  // 진입 시 상태 기본 탭 — 사용 중 > 사용예정 > 전체 순으로 자동 선택한다.
  // 회원 1명당 1회만 적용해서, 사용자가 직접 고른 탭이 목록 갱신 때 되돌아가지 않게 한다.
  useEffect(() => {
    if (!userTickets || !generalProducts) return;
    if (defaultTabMemberIdRef.current === user?.memberId) return;
    defaultTabMemberIdRef.current = user?.memberId;
    // 특정 수강권으로 진입(?ticketId=)했으면 상태·카테고리 필터 때문에 카드가 숨지 않도록 전체로 둔다.
    if (focusTicketId) { setTab('all'); setCategory('all'); return; }
    if (activeTotal > 0) setTab('active');
    else if (upcomingTotal > 0) setTab('upcoming');
    else setTab('all');
  }, [userTickets, generalProducts, user?.memberId, activeTotal, upcomingTotal, focusTicketId]);

  // 대상 카드가 그려진 뒤 화면 가운데로 한 번만 스크롤. 강조 링은 카드가 focused 로 그린다.
  // ?ticketId= 는 URL에 계속 남으므로 refetch·탭 전환마다 다시 튀지 않게 ticketId 당 1회로 제한한다.
  const scrolledTicketRef = useRef(null);
  useEffect(() => {
    if (!focusTicketId || !userTickets || !generalProducts) return undefined;
    if (scrolledTicketRef.current === focusTicketId) return undefined;
    const timer = setTimeout(() => {
      const el = document.getElementById(`member-course-ticket-${focusTicketId}`);
      if (!el) return; // 아직 안 그려졌으면 다음 데이터/탭 변경 때 재시도
      scrolledTicketRef.current = focusTicketId;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [focusTicketId, userTickets, generalProducts, tab, category]);

  // 데이터 두 개 모두 도착 전엔 스피너로 콘텐츠 깜빡임 방지.
  if (isTicketsLoading || isProductsLoading || !userTickets || !generalProducts) {
    return (
      <PageContainer>
        <CommonLoading minHeight={480} />
      </PageContainer>
    );
  }

  // ========== 회원권 카드 렌더 헬퍼 ==========
  const renderActivePass = pass => (
    <PassCard
      key={pass.memberCoursePassId}
      pass={pass}
      onUseBranchEdit={() => setUseBranchTarget({
        kind: 'pass', ticketId: pass.memberCoursePassId, productName: pass.ticketName, item: pass,
      })}
      onChangeHistoryOpen={() => setChangeHistoryTarget({
        kind: 'pass', ticketId: pass.memberCoursePassId, productName: pass.ticketName,
      })}
      onPeriodChange={() => {
        handlePassMenu(null, pass?.memberCoursePassId, 'active');
        setProductInfo({ kind: 'pass', tab: 'period' });
        openModal('openProductInfo');
      }}
      onFreeze={() => {
        handlePassMenu(null, pass?.memberCoursePassId, 'active');
        openModal('open4');
      }}
      onCancelSubscription={() => {
        handlePassMenu(null, pass?.memberCoursePassId, 'active');
        setSubCancelType('COURSE_PASS');
        openModal('openSubCancel');
      }}
    />
  );

  const renderExpiredPass = pass => (
    <PassCard
      key={pass?.memberCoursePassId}
      pass={pass}
      isExpired
      onUseBranchEdit={() => setUseBranchTarget({
        kind: 'pass', ticketId: pass.memberCoursePassId, productName: pass.ticketName, item: pass,
      })}
      onChangeHistoryOpen={() => setChangeHistoryTarget({
        kind: 'pass', ticketId: pass.memberCoursePassId, productName: pass.ticketName,
      })}
      onPeriodChange={() => {
        handlePassMenu(null, pass?.memberCoursePassId, 'expired');
        setProductInfo({ kind: 'pass', tab: 'period' });
        openModal('openProductInfo');
      }}
      onFreeze={() => {
        handlePassMenu(null, pass?.memberCoursePassId, 'expired');
        openModal('open4');
      }}
    />
  );

  const renderActiveTrial = trial => (
    <TrialTicketCard
      key={trial.memberTrialTicketId}
      trial={trial}
      onUseBranchEdit={() => setUseBranchTarget({
        kind: 'trial', ticketId: trial.memberTrialTicketId, productName: trial.trialName, item: trial,
      })}
      onChangeHistoryOpen={() => setChangeHistoryTarget({
        kind: 'trial', ticketId: trial.memberTrialTicketId, productName: trial.trialName,
      })}
      onHistoryOpen={() => setHistoryTarget({ kind: 'trial', item: trial })}
      onPeriodChange={() => {
        handleTrialSelect(trial?.memberTrialTicketId, 'active');
        setProductInfo({ kind: 'trial', tab: 'period' });
        openModal('openProductInfo');
      }}
      onBatchReservation={() => {
        handleTrialSelect(trial?.memberTrialTicketId, 'active');
        openModal('open6');
      }}
      onInstructorChange={() => {
        handleTrialSelect(trial?.memberTrialTicketId, 'active');
        openModal('open5');
      }}
    />
  );

  const renderExpiredTrial = trial => (
    <TrialTicketCard
      key={trial?.memberTrialTicketId}
      trial={trial}
      isExpired
      onUseBranchEdit={() => setUseBranchTarget({
        kind: 'trial', ticketId: trial.memberTrialTicketId, productName: trial.trialName, item: trial,
      })}
      onChangeHistoryOpen={() => setChangeHistoryTarget({
        kind: 'trial', ticketId: trial.memberTrialTicketId, productName: trial.trialName,
      })}
      onHistoryOpen={() => setHistoryTarget({ kind: 'trial', item: trial })}
      onPeriodChange={() => {
        handleTrialSelect(trial?.memberTrialTicketId, 'expired');
        setProductInfo({ kind: 'trial', tab: 'period' });
        openModal('openProductInfo');
      }}
    />
  );

  const renderActiveTicket = tick => (
    <CourseTicketCard
      key={tick.memberCourseTicketId}
      tick={tick}
      branchTrainerIds={branchTrainerIds}
      focused={String(tick.memberCourseTicketId) === String(focusTicketId)}
      onUseBranchEdit={() => setUseBranchTarget({
        kind: 'course', ticketId: tick.memberCourseTicketId, productName: tick.ticketName, item: tick,
      })}
      onChangeHistoryOpen={() => setChangeHistoryTarget({
        kind: 'course', ticketId: tick.memberCourseTicketId, productName: tick.ticketName,
        // 변경 이력은 소유자 회원 ID 로 저장되므로 공유받은 카드는 소유자 기준으로 조회한다.
        ownerMemberId: tick.sharedFrom?.memberId,
      })}
      onHistoryOpen={() => setHistoryTarget({ kind: 'course', item: tick })}
      onPeriodChange={() => {
        handleTicketMenu(null, tick?.memberCourseTicketId, 'active');
        setProductInfo({ kind: 'course', tab: 'period' });
        openModal('openProductInfo');
      }}
      onFreeze={() => {
        handleTicketMenu(null, tick?.memberCourseTicketId, 'active');
        openModal('open3');
      }}
      onInstructorChange={() => {
        handleTicketMenu(null, tick?.memberCourseTicketId, 'active');
        openModal('open5');
      }}
      onReservationSetting={() => {
        handleTicketMenu(null, tick?.memberCourseTicketId, 'active');
        setProductInfo({ kind: 'course', tab: 'reservation' });
        openModal('openProductInfo');
      }}
      onBatchReservation={() => {
        handleTicketMenu(null, tick?.memberCourseTicketId, 'active');
        openModal('open6');
      }}
    />
  );

  const renderExpiredTicket = tick => (
    <CourseTicketCard
      key={tick?.memberCourseTicketId}
      tick={tick}
      branchTrainerIds={branchTrainerIds}
      focused={String(tick?.memberCourseTicketId) === String(focusTicketId)}
      isExpired
      onUseBranchEdit={() => setUseBranchTarget({
        kind: 'course', ticketId: tick.memberCourseTicketId, productName: tick.ticketName, item: tick,
      })}
      onChangeHistoryOpen={() => setChangeHistoryTarget({
        kind: 'course', ticketId: tick.memberCourseTicketId, productName: tick.ticketName,
        // 변경 이력은 소유자 회원 ID 로 저장되므로 공유받은 카드는 소유자 기준으로 조회한다.
        ownerMemberId: tick.sharedFrom?.memberId,
      })}
      onHistoryOpen={() => setHistoryTarget({ kind: 'course', item: tick })}
      onPeriodChange={() => {
        handleTicketMenu(null, tick?.memberCourseTicketId, 'expired');
        setProductInfo({ kind: 'course', tab: 'period', expired: true });
        openModal('openProductInfo');
      }}
      onFreeze={() => {
        handleTicketMenu(null, tick?.memberCourseTicketId, 'expired');
        openModal('open3');
      }}
    />
  );

  // ========== 일반상품 카드 렌더 헬퍼 ==========

  const renderPeriodCard = (pass, isExpiredCard = false) => (
    <ProductCard
      key={pass.memberProductPassId}
      kind="product"
      item={pass}
      isExpired={isExpiredCard}
      onPeriodChange={() => handleProductPeriodChange(pass)}
      onStop={() => handleProductStop(pass)}
    />
  );

  // 회원권 측 컨텍스트 메뉴 (이용권 / 수강권)
  const passContextMenu = (
    <Menu
      anchorEl={passAnchor}
      open={Boolean(passAnchor)}
      onClose={closeMenus}
      MenuListProps={{ 'aria-labelledby': 'pass-menu-button' }}>
      <MenuItem
        onClick={() => {
          closeMenus();
          setProductInfo({ kind: 'pass', tab: 'period' });
        openModal('openProductInfo');
        }}>
        기간 / 횟수 변경
      </MenuItem>
      {ticket?.status !== 'INACTIVE' && (
        <MenuItem
          disabled={!ticket?.isPossibleStop}
          onClick={() => {
            closeMenus();
            openModal('open4');
          }}>
          이용권 중지 설정
        </MenuItem>
      )}
    </Menu>
  );

  const ticketContextMenu = (
    <Menu
      anchorEl={ticketAnchor}
      open={Boolean(ticketAnchor)}
      onClose={closeMenus}
      MenuListProps={{ 'aria-labelledby': 'ticket-menu-button' }}>
      <MenuItem
        onClick={() => {
          closeMenus();
          setProductInfo({ kind: 'course', tab: 'period' });
        openModal('openProductInfo');
        }}>
        기간 / 횟수 변경
      </MenuItem>
      {ticket?.status !== 'INACTIVE' && (
        <MenuItem
          disabled={!ticket?.isPossibleStop}
          onClick={() => {
            closeMenus();
            openModal('open3');
          }}>
          수강권 중지 설정
        </MenuItem>
      )}
      {ticket?.classType === 'GROUP_LESSON' ? (
        <MenuItem
          onClick={() => {
            closeMenus();
            setProductInfo({ kind: 'course', tab: 'reservation' });
        openModal('openProductInfo');
          }}>
          수강권 예약 설정
        </MenuItem>
      ) : (
        <MenuItem
          disabled={
            ticket?.classType !== 'PRIVATE_LESSON' &&
            ticket?.classType !== 'DUET_LESSON' &&
            ticket?.classType !== 'OT'
          }
          onClick={() => {
            closeMenus();
            openModal('open5');
          }}>
          담당 강사 변경
        </MenuItem>
      )}
      <MenuItem
        onClick={() => {
          closeMenus();
          openModal('open6');
        }}>
        레슨 일괄예약
      </MenuItem>
    </Menu>
  );

  // ========== 탭 상태 + 탭별 표시 목록 ==========
  const isAllTab = tab === 'all';
  const isExpired = tab === 'expired';
  const isUpcomingTab = tab === 'upcoming';

  // '전체' 탭은 사용 중 + 사용예정 + 만료를 모두 합쳐 보여준다.
  // 사용예정 탭은 active 안에서 시작일 미래만, 그 외는 기존과 동일.
  const passesForTab = isAllTab
    ? [...activePasses, ...upcomingPasses, ...expiredPasses]
    : isExpired
    ? expiredPasses
    : isUpcomingTab
    ? upcomingPasses
    : activePasses;
  const trialTicketsForTab = isAllTab
    ? [...activeTrialTickets, ...upcomingTrialTickets, ...expiredTrialTickets]
    : isExpired
    ? expiredTrialTickets
    : isUpcomingTab
    ? upcomingTrialTickets
    : activeTrialTickets;
  const ticketsForTab = isAllTab
    ? [...activeTickets, ...upcomingTickets, ...expiredTickets]
    : isExpired
    ? expiredTickets
    : isUpcomingTab
    ? upcomingTickets
    : activeTickets;
  const lockersForTab = isAllTab
    ? [...activeLockers, ...upcomingLockers, ...expiredLockers]
    : isExpired
    ? expiredLockers
    : isUpcomingTab
    ? upcomingLockers
    : activeLockers;
  const periodPassesForTab = isAllTab
    ? [...activePeriodPasses, ...upcomingPeriodPasses, ...expiredPeriodPasses]
    : isExpired
    ? expiredPeriodPasses
    : isUpcomingTab
    ? upcomingPeriodPasses
    : activePeriodPasses;

  // '전체' 탭에서는 항목별로 만료 여부를 판별해 알맞은 카드(사용 중/만료)로 렌더한다.
  const expiredPassIds = new Set(expiredPasses.map(p => p.memberCoursePassId));
  const expiredTrialIds = new Set(expiredTrialTickets.map(x => x.memberTrialTicketId));
  const expiredTicketIds = new Set(expiredTickets.map(x => x.memberCourseTicketId));
  const passRenderer = isAllTab
    ? pass => (expiredPassIds.has(pass?.memberCoursePassId) ? renderExpiredPass(pass) : renderActivePass(pass))
    : isExpired
    ? renderExpiredPass
    : renderActivePass;
  const trialRenderer = isAllTab
    ? trial => (expiredTrialIds.has(trial?.memberTrialTicketId) ? renderExpiredTrial(trial) : renderActiveTrial(trial))
    : isExpired
    ? renderExpiredTrial
    : renderActiveTrial;
  const ticketRenderer = isAllTab
    ? tick => (expiredTicketIds.has(tick?.memberCourseTicketId) ? renderExpiredTicket(tick) : renderActiveTicket(tick))
    : isExpired
    ? renderExpiredTicket
    : renderActiveTicket;
  // 같은 자리(branchLockerId) 카드는 대표 1장만 펼쳐 두고 나머지는 "연장 내역 N건" 토글 뒤에 접는다.
  // 미배정 패스는 branchLockerId 가 없으니 패스별로 독립 묶음.
  const buildLockerSpotGroups = list => {
    const groups = new Map();
    list.forEach(item => {
      const key = item.branchLockerId != null ? `spot-${item.branchLockerId}` : `pass-${item.memberLockerPassId}`;
      if (groups.has(key)) return;
      // 자리 카드는 그 자리의 이용권 전체를 담는다. 상태 필터는 "이 자리를 보여줄지"만 정하고,
      // 카드 안 스택까지 잘라내면 한 자리에 이용권이 여러 개일 때 나머지가 사라진다.
      // 단 만료 탭에서는 만료된 것만, 그 외 탭에서는 만료가 아닌 것만 묶는다.
      // 상태 필터는 "이 자리를 목록에 띄울지"만 정한다. 자리 안 이용권 하나라도 필터에 걸리면
      // 그 자리는 뜨고, 카드에는 그 자리의 이용권을 전부 담는다.
      // 사용중 1건 + 만료 2건인 자리를 사용중 탭에서 골라도 3건이 다 보여야 한다 —
      // 만료를 잘라내면 그 자리의 연체 이력이 화면에서 사라진다.
      const items =
        item.branchLockerId != null
          ? lockerPasses.filter(
              lp =>
                lp.branchLockerId === item.branchLockerId &&
                (isActiveStatus(lp.status) || isExpiredStatus(lp.status))
            )
          : [item];
      groups.set(key, { key, items: items.length > 0 ? items : [item] });
    });

    // 정렬 — 배정된 자리 먼저, 그 안에서 빨리 끝나는 순, 미배정은 뒤로.
    // 자리에 이용권이 여러 개면 아직 안 끝난 것 중 가장 빠른 종료일을 기준으로 본다.
    // 전부 만료인 자리는 배정 안에서도 뒤로 보내되, 최근에 끝난 것부터 놓는다.
    const sortKey = g => {
      const assigned = g.items.some(lp => lp.branchLockerId != null) ? 0 : 1;
      const living = g.items.filter(lp => !isExpiredLocker(lp) && lp.expireDate);
      if (living.length > 0) {
        return [assigned, 0, Math.min(...living.map(lp => dayjs(lp.expireDate).valueOf()))];
      }
      const ends = g.items.filter(lp => lp.expireDate).map(lp => dayjs(lp.expireDate).valueOf());
      return [assigned, 1, ends.length > 0 ? -Math.max(...ends) : 0];
    };
    return Array.from(groups.values()).sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
    });
  };

  const lockerSpotGroupsForTab = buildLockerSpotGroups(lockersForTab);

  // 라커 1자리 = 카드 1장 (이용권 스택 + 단일 선택). 만료 여부는 그룹 전체가 만료일 때만.
  const lockerGroupRenderer = group => {
    // 스택이 어느 탭에서든 자리 전체를 담으므로, 회색 처리도 탭이 아니라 내용으로 판단한다.
    // 만료 탭이라도 그 자리에 살아 있는 이용권이 있으면 카드를 죽은 것처럼 보이면 안 된다.
    const groupExpired = group.items.every(isExpiredLocker);
    return (
      <LockerSpotCard
        key={group.key}
        group={group.items}
        isExpired={groupExpired}
        onAssign={handleLockerAssign}
        onMove={handleLockerAssign}
        onUnassign={handleLockerUnassign}
        onPeriodChange={handleProductPeriodChange}
        onStop={handleProductStop}
        onExtend={onLockerExtend}
      />
    );
  };
  const periodRenderer = pass => renderPeriodCard(pass, isAllTab ? isExpiredStatus(pass.status) : isExpired);

  // 탭별 빈 상태 메시지 prefix. ('전체'는 상태 무관 → '등록된')
  const emptyPrefix = isAllTab ? '등록된' : isExpired ? '만료된' : isUpcomingTab ? '사용예정' : '사용 중인';
  const emptyMessageFor = kind => t(`${emptyPrefix} ${kind}이 없습니다.`);

  // ========== 단일 카테고리 sub-section 렌더 (사이드바 leaf 클릭 시) ==========
  const renderPassOnly = () => (
    <ProductSubsection
      title={t('이용권')}
      count={passesForTab.length}
      items={passesForTab}
      emptyMessage={emptyMessageFor('이용권')}
      isExpired={isExpired}
      renderItem={passRenderer}>
      {!isExpired && passContextMenu}
    </ProductSubsection>
  );

  const renderTrialOnly = () => (
    <ProductSubsection
      title={t('일일권')}
      count={trialTicketsForTab.length}
      items={trialTicketsForTab}
      emptyMessage={emptyMessageFor('일일권')}
      isExpired={isExpired}
      renderItem={trialRenderer}
    />
  );

  const renderTicketOnly = () => (
    <ProductSubsection
      title={t('수강권')}
      count={ticketsForTab.length}
      items={ticketsForTab}
      emptyMessage={emptyMessageFor('수강권')}
      isExpired={isExpired}
      renderItem={ticketRenderer}>
      {!isExpired && ticketContextMenu}
    </ProductSubsection>
  );

  const renderLockerOnly = () => (
    <ProductSubsection
      title={t('라커')}
      count={lockersForTab.length}
      items={lockerSpotGroupsForTab}
      emptyMessage={emptyMessageFor('라커')}
      isExpired={isExpired}
      renderItem={lockerGroupRenderer}
    />
  );

  const renderProductOnly = () => (
    <ProductSubsection
      title={t('일반상품')}
      count={periodPassesForTab.length}
      items={periodPassesForTab}
      emptyMessage={emptyMessageFor('일반상품')}
      isExpired={isExpired}
      renderItem={periodRenderer}
    />
  );

  // ========== 회원권 / 일반상품 Section 렌더 — 위 sub-section 들을 그대로 합친 컨테이너 ==========
  const renderTicketsSection = (total, action) => (
    <Section>
      <SectionHeader>
        <SectionTitle muted={isExpired}>{t('회원권')}</SectionTitle>
        <SectionTotal muted={isExpired}>총 {total}건</SectionTotal>
        {action && <SectionHeaderAction>{action}</SectionHeaderAction>}
      </SectionHeader>
      {renderPassOnly()}
      {renderTrialOnly()}
      {renderTicketOnly()}
    </Section>
  );

  const renderProductsSection = (total, action) => (
    <Section>
      <SectionHeader>
        <SectionTitle muted={isExpired}>{t('일반상품')}</SectionTitle>
        <SectionTotal muted={isExpired}>총 {total}건</SectionTotal>
        {action && <SectionHeaderAction>{action}</SectionHeaderAction>}
      </SectionHeader>
      {renderLockerOnly()}
      {renderProductOnly()}
    </Section>
  );

  // ========== 사이드바 카테고리 → 본문 콘텐츠 디스패처 ==========
  const ticketsSectionTotal = isAllTab
    ? ticketsActiveTotal + ticketsUpcomingTotal + ticketsExpiredTotal
    : isExpired
    ? ticketsExpiredTotal
    : isUpcomingTab
    ? ticketsUpcomingTotal
    : ticketsActiveTotal;
  const productsSectionTotal = isAllTab
    ? productsActiveTotal + productsUpcomingTotal + productsExpiredTotal
    : isExpired
    ? productsExpiredTotal
    : isUpcomingTab
    ? productsUpcomingTotal
    : productsActiveTotal;

  // 상품일괄중지 버튼 — 첫 섹션 헤더(회원권/leaf) 우측에 인라인으로 삽입한다.
  const bulkStopButton = (
    <BulkStopButton onClick={() => setBulkStopModalOpen(true)}>{t('상품일괄중지')}</BulkStopButton>
  );

  const renderCategoryContent = () => {
    if (category === 'all') {
      return (
        <Zone>
          {renderTicketsSection(ticketsSectionTotal, bulkStopButton)}
          {renderProductsSection(productsSectionTotal)}
        </Zone>
      );
    }
    if (category === 'tickets') return renderTicketsSection(ticketsSectionTotal, bulkStopButton);
    if (category === 'products') return renderProductsSection(productsSectionTotal, bulkStopButton);
    if (category === 'pass')
      return (
        <>
          <LeafActionRow>{bulkStopButton}</LeafActionRow>
          {renderPassOnly()}
        </>
      );
    if (category === 'trial')
      return (
        <>
          <LeafActionRow>{bulkStopButton}</LeafActionRow>
          {renderTrialOnly()}
        </>
      );
    if (category === 'ticket')
      return (
        <>
          <LeafActionRow>{bulkStopButton}</LeafActionRow>
          {renderTicketOnly()}
        </>
      );
    if (category === 'locker')
      return (
        <>
          <LeafActionRow>{bulkStopButton}</LeafActionRow>
          {renderLockerOnly()}
        </>
      );
    if (category === 'product')
      return (
        <>
          <LeafActionRow>{bulkStopButton}</LeafActionRow>
          {renderProductOnly()}
        </>
      );
    return null;
  };

  // 사이드바 카운트:
  // - active/upcoming/expired: 두 카테고리 합산한 글로벌 상태 카운트 (탭 차원).
  // - total/tickets/products/leaf들: 현재 선택된 탭(상태) 기준의 카테고리 카운트.
  const sidebarCounts = {
    statusAll: activeTotal + upcomingTotal + expiredTotal,
    active: activeTotal,
    upcoming: upcomingTotal,
    expired: expiredTotal,
    total: ticketsSectionTotal + productsSectionTotal,
    tickets: ticketsSectionTotal,
    products: productsSectionTotal,
    pass: passesForTab.length,
    trial: trialTicketsForTab.length,
    ticket: ticketsForTab.length,
    locker: lockersForTab.length,
    product: periodPassesForTab.length,
  };

  return (
    <PageContainer>
      {/* 좌측 사이드바(상태 + 카테고리 필터) + 우측 본문 */}
      <LayoutShell>
        <CategorySidebar
          tab={tab}
          onTabSelect={setTab}
          category={category}
          onCategorySelect={setCategory}
          counts={sidebarCounts}
        />
        <MainContent>{renderCategoryContent()}</MainContent>
      </LayoutShell>

      {/* 회원권 변경 모달들 — modal.openX 키와 setModal은 자식 모달과 공유되는 컨트랙트. */}
      {modal.openProductInfo && (
        <ProductInfoEditModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
          kind={productInfo.kind}
          initialTab={productInfo.tab}
          expired={!!productInfo.expired}
        />
      )}
      {modal.open1 && (
        <ChangeTPeriodModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
        />
      )}
      {modal.open2 && (
        <ChangePassPeriodModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
        />
      )}
      {modal.openSubCancel && (
        <SubscriptionCancellationModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          passType={subCancelType}
          getAllTickets={refreshAfterMutation}
        />
      )}
      {modal.open3 && (
        <ChangeTFreezyModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
        />
      )}
      {modal.open4 && (
        <ChangePFreezyModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
        />
      )}
      {modal.open5 && (
        <ChangeInstructorModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
        />
      )}
      {modal.openReservationSetting && (
        <ReservationSettingModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
        />
      )}
      {changeHistoryTarget && (
        <TicketChangeHistoryModal
          open
          target={changeHistoryTarget}
          memberId={changeHistoryTarget.ownerMemberId ?? user?.memberId}
          branchId={bigUser?.branch?.branchId}
          token={bigUser.token}
          onClose={() => setChangeHistoryTarget(null)}
        />
      )}
      {useBranchTarget && (
        <UseBranchEditModal
          open
          target={useBranchTarget}
          memberId={user?.memberId}
          token={bigUser.token}
          onClose={() => setUseBranchTarget(null)}
          onSaved={refreshAfterMutation}
        />
      )}
      {modal.open6 && (
        <ChangeLessonBatchModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
        />
      )}
      {modal.openFindLesson && (
        <FindLessonModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
        />
      )}
      {modal.open7 && (
        <ChangeTrialPeriodModal
          modal={modal}
          setModal={setModal}
          bigUser={bigUser}
          ticket={ticket}
          user={user}
          getAllTickets={refreshAfterMutation}
        />
      )}

      {/* 수강권/일일권 카드 클릭 → 해당 이용권으로 잡힌 예약·수업 내역 */}
      <TicketLessonHistoryModal
        open={Boolean(historyTarget)}
        onClose={() => setHistoryTarget(null)}
        ticket={historyTarget?.item}
        kind={historyTarget?.kind}
        user={user}
        bigUser={bigUser}
        onMutated={refreshAfterMutation}
      />

      {/* 일반상품 기간 변경 모달 — open/onClose API. */}
      <ChangeGeneralProductPeriodModal
        open={productPeriodModalOpen}
        onClose={closeProductPeriodModal}
        ticket={selectedProduct}
        memberId={user?.memberId}
        getAllUserTickets={refreshAfterMutation}
        spotPasses={
          selectedProduct?.branchLockerId
            ? lockerPasses
                .filter(lp => lp.branchLockerId === selectedProduct.branchLockerId)
                .map(lp => ({ ...lp, expired: isExpiredLocker(lp) }))
            : []
        }
      />

      {/* 일반상품 중지 설정 모달 — open/onClose API. */}
      <ChangeGeneralProductStopModal
        open={productStopModalOpen}
        onClose={closeProductStopModal}
        ticket={selectedProduct}
        memberId={user?.memberId}
        getAllUserTickets={refreshAfterMutation}
      />

      {/* 일괄중지 모달 — 수강권/이용권/라커/일반상품을 함께 선택해 동일 기간으로 중지. */}
      <BulkStopModal
        open={bulkStopModalOpen}
        onClose={() => setBulkStopModalOpen(false)}
        userTickets={userTickets}
        generalProducts={generalProducts}
        memberId={user?.memberId}
        bigUser={bigUser}
        getAllUserTickets={refreshAfterMutation}
      />

      {/* 라커 자리 배정 / 자리 변경 — 판매등록과 같은 자리 선택 모달을 재사용한다.
          본인 점유 자리는 파란색으로 선택 가능 — 미배정 패스를 기존 자리에 붙이면
          연장이 된다(기간이 겹치면 백엔드가 406 으로 거부). 타 회원 자리만 비활성.
          이 카드가 배정된 현재 자리는 '선택됨'으로 미리 표시해 어디에서 옮기는지 보여준다. */}
      <LockerSelectModal
        memberId={user?.memberId}
        title={lockerAssignTarget?.branchLockerId ? t('자리 변경') : t('라커 배정')}
        confirmText={lockerAssignTarget?.branchLockerId ? t('이동하기') : t('배정하기')}
        open={lockerAssignModalOpen}
        onClose={closeLockerAssignModal}
        onSelect={handleLockerSelected}
        primaryPassId={lockerAssignTarget?.memberLockerPassId}
        passOptions={
          lockerAssignTarget?.branchLockerId
            ? lockerPasses.filter(
                lp => lp.branchLockerId === lockerAssignTarget.branchLockerId && isActiveStatus(lp.status)
              )
            : []
        }
        initialLocker={
          lockerAssignTarget?.branchLockerId
            ? {
                lockerId: lockerAssignTarget.branchLockerId,
                lockerNumber: lockerAssignTarget.lockerNumber,
                lockerAreaName: lockerAssignTarget.lockerAreaName,
              }
            : null
        }
      />

      {/* 이어서 배정 — 본인 자리의 마지막 이용 종료일 +1 을 기본으로 시작일을 고른다 */}
      <LockerAttachDateModal
        open={Boolean(lockerAttachTarget)}
        onClose={closeLockerAttach}
        onConfirm={confirmLockerAttach}
        lockerNumber={lockerAttachTarget?.locker?.lockerNumber}
        spotPasses={
          lockerAttachTarget
            ? lockerPasses
                // 만료도 함께 넘긴다. 백엔드 겹침 검증이 만료까지 보므로 여기서 빼면
                // 화면은 통과인데 서버가 406 을 던진다. 만료 행은 모달에서 고정 잠금된다.
                .filter(
                  lp =>
                    lp.branchLockerId === lockerAttachTarget.locker.lockerId &&
                    (isActiveStatus(lp.status) || isExpiredStatus(lp.status))
                )
                .map(lp => ({ ...lp, expired: isExpiredLocker(lp) }))
            : []
        }
        movingPasses={lockerAttachTarget?.passes ?? []}
      />

      {/* 배정 해제 확인 */}
      <ConfirmModal
        open={Boolean(lockerUnassignTarget)}
        onClose={() => setLockerUnassignTarget(null)}
        onConfirm={confirmLockerUnassign}
        message={
          lockerUnassignTarget?.lockerNumber
            ? `${lockerUnassignTarget.lockerNumber}번 라커 배정을 해제하시겠습니까?`
            : t('라커 배정을 해제하시겠습니까?')
        }
        highlightText="해제"
        highlightColor="#F44336"
        cancelText="닫기"
        confirmText="해제"
        confirmColor="#F44336"
      />
    </PageContainer>
  );
});

CoursTicketDetail.displayName = 'CoursTicketDetail';

export default CoursTicketDetail;
